import { useCallback, useEffect, useState } from 'react'
import { useStore } from 'react-redux'
import { surveyService } from '../services/surveyService'
import { constants } from '../Constants/constants'
import _ from 'lodash'

/**
 * Custom hook to manage survey list state
 * Handles data fetching, filtering, pagination, and sorting
 */
export const useSurveyList = (initialFilters = []) => {
  const store = useStore()
  const [list, setList] = useState([])
  const [listLength, setListLength] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const [pagination, setPagination] = useState({
    sort: 'updateTimestamp',
    order: 'DESC',
    pageSize: 10,
    page: 0,
    filter: {},
  })

  const [filters, setFilters] = useState(initialFilters)

  /**
   * Build query parameters from current state
   */
  const buildQueryParams = useCallback(() => {
    const filterObj = {}
    filters.forEach((filterItem) => {
      if (filterItem.value !== '' && filterItem.value !== undefined) {
        filterObj[filterItem.name] = filterItem.value
      }
    })

    return {
      sort: pagination.sort,
      order: pagination.order,
      page: pagination.page,
      perPage: pagination.pageSize,
      filter: JSON.stringify(filterObj),
    }
  }, [filters, pagination])

  /**
   * Fetch survey list
   */
  const fetchList = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const queryParams = buildQueryParams()
      const response = await surveyService.fetchList(queryParams, store)
      
      setList(response)
      setListLength(response?.length || 0)
    } catch (err) {
      setError(err)
      console.error('Error fetching survey list:', err)
    } finally {
      setLoading(false)
    }
  }, [buildQueryParams, store])

  /**
   * Update filter value
   */
  const updateFilter = useCallback((filterName, value) => {
    setFilters((prev) =>
      prev.map((f) =>
        f.name === filterName ? { ...f, value } : f
      )
    )
    // Reset to first page when filters change
    setPagination((prev) => ({ ...prev, page: 0 }))
  }, [])

  /**
   * Update multiple filters at once
   */
  const updateFilters = useCallback((newFilters) => {
    setFilters(newFilters)
    setPagination((prev) => ({ ...prev, page: 0 }))
  }, [])

  /**
   * Update pagination
   */
  const updatePagination = useCallback((newPagination) => {
    setPagination((prev) => ({ ...prev, ...newPagination }))
  }, [])

  /**
   * Change page
   */
  const changePage = useCallback((page) => {
    setPagination((prev) => ({ ...prev, page }))
  }, [])

  /**
   * Change rows per page
   */
  const changeRowsPerPage = useCallback((pageSize) => {
    setPagination((prev) => ({ ...prev, pageSize, page: 0 }))
  }, [])

  /**
   * Change sort column
   */
  const changeSort = useCallback((sort, order = 'DESC') => {
    setPagination((prev) => ({ ...prev, sort, order }))
  }, [])

  /**
   * Refetch the list
   */
  const refetch = useCallback(() => {
    fetchList()
  }, [fetchList])

  // Fetch list whenever pagination or filters change
  useEffect(() => {
    fetchList()
  }, [fetchList])

  return {
    list,
    listLength,
    loading,
    error,
    pagination,
    filters,
    updateFilter,
    updateFilters,
    updatePagination,
    changePage,
    changeRowsPerPage,
    changeSort,
    refetch,
    setPagination,
  }
}
